/**
 * High-Yield Savings Account rate snapshot — Phase 8 Session 3.
 *
 * Manually-curated rate table for top-20 HYSAs / cash-management
 * accounts the household might compare against. Rates are APY in basis
 * points (450 = 4.50%). All accounts FDIC-insured (SIPC for the
 * brokerage cash-management exception flagged below).
 *
 * **as_of** is the snapshot date — UI surfaces a "snapshot stale"
 * warning if more than 6 months old (HYSAs churn fast).
 *
 * Lives in `web/` per the 5.1 lock — web does NOT import from
 * @hearth/shared. Banking hygiene v1 is pure-frontend; if we ever
 * want server-side stale-rate alerting, the data + helpers shift to
 * shared/ at that time.
 */

export interface HysaRate {
  readonly name: string;
  readonly issuer: string;
  readonly apy_bp: number;
  readonly fdic_insured: boolean;
  readonly min_balance_cents: number;
  readonly notes?: string;
}

export const HYSA_RATES_AS_OF = "2026-05-08";

export const HYSA_RATES: readonly HysaRate[] = [
  {
    name: "Marcus Online Savings",
    issuer: "Goldman Sachs",
    apy_bp: 425,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "Ally Online Savings",
    issuer: "Ally Bank",
    apy_bp: 410,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "Discover Online Savings",
    issuer: "Discover Bank",
    apy_bp: 415,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "Capital One 360 Performance Savings",
    issuer: "Capital One",
    apy_bp: 400,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "American Express HYSA",
    issuer: "American Express",
    apy_bp: 425,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "SoFi Checking + Savings",
    issuer: "SoFi Bank",
    apy_bp: 425,
    fdic_insured: true,
    min_balance_cents: 0,
    notes: "Higher tier requires direct deposit",
  },
  {
    name: "CIT Platinum Savings",
    issuer: "CIT Bank",
    apy_bp: 460,
    fdic_insured: true,
    min_balance_cents: 500000,
    notes: "Min $5,000 for top tier",
  },
  {
    name: "Wealthfront Cash",
    issuer: "Wealthfront Brokerage (program banks)",
    apy_bp: 450,
    fdic_insured: true,
    min_balance_cents: 0,
    notes: "Up to $8M aggregate FDIC via program banks",
  },
  {
    name: "Betterment Cash Reserve",
    issuer: "Betterment Brokerage (program banks)",
    apy_bp: 440,
    fdic_insured: true,
    min_balance_cents: 0,
    notes: "Up to $2M aggregate FDIC",
  },
  {
    name: "Robinhood Gold Cash Sweep",
    issuer: "Robinhood (program banks)",
    apy_bp: 450,
    fdic_insured: true,
    min_balance_cents: 0,
    notes: "Requires Robinhood Gold ($5/mo)",
  },
  {
    name: "Fidelity SPAXX (Government MMF)",
    issuer: "Fidelity",
    apy_bp: 470,
    fdic_insured: false,
    min_balance_cents: 0,
    notes: "MMF — SIPC, not FDIC. Variable 7-day yield.",
  },
  {
    name: "Vanguard VMFXX (Federal MMF)",
    issuer: "Vanguard",
    apy_bp: 475,
    fdic_insured: false,
    min_balance_cents: 0,
    notes: "MMF — SIPC, not FDIC. Variable 7-day yield.",
  },
  {
    name: "Charles Schwab Bank Savings",
    issuer: "Charles Schwab Bank",
    apy_bp: 48,
    fdic_insured: true,
    min_balance_cents: 0,
    notes: "Schwab default sweep is intentionally low — move to MMF.",
  },
  {
    name: "Public High-Yield Cash",
    issuer: "Public Investing (program banks)",
    apy_bp: 480,
    fdic_insured: true,
    min_balance_cents: 0,
    notes: "Up to $5M aggregate FDIC",
  },
  {
    name: "UFB Direct Secure Savings",
    issuer: "Axos Bank",
    apy_bp: 510,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "Bask Bank Interest Savings",
    issuer: "Texas Capital Bank",
    apy_bp: 485,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "Bread Savings",
    issuer: "Comenity Capital Bank",
    apy_bp: 475,
    fdic_insured: true,
    min_balance_cents: 10000,
  },
  {
    name: "TAB Bank High Yield Savings",
    issuer: "TAB Bank",
    apy_bp: 502,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "Synchrony High Yield Savings",
    issuer: "Synchrony Bank",
    apy_bp: 435,
    fdic_insured: true,
    min_balance_cents: 0,
  },
  {
    name: "Citizens Access Online Savings",
    issuer: "Citizens Bank",
    apy_bp: 415,
    fdic_insured: true,
    min_balance_cents: 500,
  },
] as const;
